<script lang="ts">
    import { VpDeleteBinIcon } from '@decathlon-experience/icons/svelte';
    import Sortable from 'sortablejs';
    import { createEventDispatcher, onMount } from 'svelte';
    import { writable } from 'svelte/store';

    import { trans } from '$assets/translator.js';
    import DeleteConfirmationModal from '$backoffice/components/DeleteConfirmationModal.svelte';
    import {
        imageOptimize,
        isLowResolution,
    } from '$backoffice/views/MediaManager/utils';
    import DragHandleIcon from '$common/icons/DragHandleIcon.svelte';
    import cn from '$common/utils/cn';

    import type { ImageItem } from './types';

    export let items: ImageItem[];
    export let showCoverLabel: boolean = false;

    const dispatch = createEventDispatcher<{
        reorder: { items: ImageItem[] };
        remove: { tempId: string };
        preview: { item: ImageItem };
        editFocalPoint: { tempId: string };
        editCrop: { tempId: string };
    }>();

    let listElement: HTMLElement;
    // Resolution keyed by tempId
    type Resolution = { width: number; height: number } | null | undefined;
    // eslint-disable-next-line svelte/prefer-svelte-reactivity -- reactivity is triggered by reassigning the variable
    let resolutionMap: Map<string, Resolution> = new Map();

    onMount(() => {
        const sortable = new Sortable(listElement, {
            animation: 150,
            ghostClass: 'opacity-60',
            handle: '.drag-handle', // Add handle class selector
            filter: '.uploading',
            onEnd: (evt) => {
                if (evt.oldIndex === undefined || evt.newIndex === undefined) {
                    return;
                }

                const updatedItems = [...items];
                const movedItem = updatedItems.splice(evt.oldIndex, 1)[0];
                updatedItems.splice(evt.newIndex, 0, movedItem);

                // Update positions
                const reorderedItems = updatedItems.map((item, index) => ({
                    ...item,
                    position: index,
                }));

                dispatch('reorder', { items: reorderedItems });
            },
        });

        return () => {
            sortable.destroy();
        };
    });

    const getDisplayUrl = (item: ImageItem): string | null => {
        if (item.status === 'uploading' && item.blobUrl) {
            return item.blobUrl;
        }
        if (item.status === 'uploaded' && item.path) {
            // Optimize the image for 400x400 thumbnail with AVIF format
            return imageOptimize(item.path, '400x400', 'avif');
        }
        return null;
    };

    // Modal state for delete confirmation
    const isDeleteModalOpen = writable(false);
    let pendingDeleteId: string | null = null;

    const openDeleteModal = (tempId: string) => {
        pendingDeleteId = tempId;
        $isDeleteModalOpen = true;
    };

    const handleDeleteConfirm = () => {
        if (pendingDeleteId) {
            dispatch('remove', { tempId: pendingDeleteId });
            pendingDeleteId = null;
        }
    };

    const handleDeleteCancel = () => {
        pendingDeleteId = null;
    };

    // Whenever items change, fetch resolution for any uploaded item not yet measured
    $: {
        for (const item of items) {
            if (
                item.status === 'uploaded' &&
                item.path &&
                !resolutionMap.has(item.tempId)
            ) {
                if (item.width && item.height) {
                    resolutionMap.set(item.tempId, {
                        width: item.width,
                        height: item.height,
                    });
                    continue;
                }

                // Mark as pending immediately to avoid duplicate requests
                resolutionMap.set(item.tempId, undefined);
                const capturedTempId = item.tempId;
                const img = new Image();
                img.onload = () => {
                    resolutionMap.set(capturedTempId, {
                        width: img.naturalWidth,
                        height: img.naturalHeight,
                    });
                    resolutionMap = resolutionMap; // trigger reactivity
                };
                img.src = item.path;
            }
        }
    }

    const getResolutionBadge = (
        tempId: string,
    ): { label: string; class: string } | null => {
        if (!resolutionMap.has(tempId)) return null; // still loading
        const res = resolutionMap.get(tempId) ?? null;
        if (!res) return null; // failed to load
        if (!isLowResolution(res.width, res.height)) return null; // resolution is fine
        return {
            label: trans('form.multi_image_upload.upload_dimension_warning')
                .replace('%width%', res.width.toString())
                .replace('%height%', res.height.toString()),
            class: 'bg-status-warning-quiet text-status-warning border-status-warning border border-solid',
        };
    };
</script>

<ul bind:this={listElement} class="grid grid-cols-4 gap-s">
    {#each items as item, i (item.tempId)}
        {@const displayUrl = getDisplayUrl(item)}
        <li
            class={cn(
                'group relative border border-border-quiet',
                item.status === 'uploading' && 'uploading',
            )}
        >
            {#if item.status === 'uploading'}
                {#if displayUrl}
                    <img
                        src={displayUrl}
                        alt="File uploading..."
                        class="h-full w-full object-cover opacity-40"
                        loading="lazy"
                        decoding="async"
                    />
                    <div
                        class="pointer-events-none absolute inset-0 z-20 flex items-center justify-center rounded-lg bg-black bg-opacity-40"
                    >
                        <svg
                            class="h-12 w-12 animate-spin text-white"
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                        >
                            <circle
                                class="opacity-25"
                                cx="12"
                                cy="12"
                                r="10"
                                stroke="currentColor"
                                stroke-width="4"
                            ></circle>
                            <path
                                class="opacity-75"
                                fill="currentColor"
                                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                            ></path>
                        </svg>
                    </div>
                {/if}
            {:else if item.status === 'uploaded' && displayUrl}
                <button
                    type="button"
                    class="drag-handle flex w-full cursor-grab justify-center py-xs"
                >
                    <DragHandleIcon classValue="size-xl rotate-90" />
                </button>
                <button
                    type="button"
                    on:click={() => dispatch('preview', { item })}
                    class="focus:ring-border-focus relative aspect-square w-full cursor-zoom-in focus:outline-none focus:ring-2 focus:ring-offset-2"
                >
                    <img
                        src={displayUrl}
                        alt=""
                        class="absolute inset-0 h-full w-full object-cover"
                        style={item.focalPoint
                            ? `object-position: ${item.focalPoint.x}% ${item.focalPoint.y}%`
                            : undefined}
                        loading="lazy"
                        decoding="async"
                    />
                    {#if i === 0 && showCoverLabel}
                        <span
                            class="shadow-sm absolute left-2 top-2 z-30 rounded-full border border-blue-brand bg-white px-xs py-2xs text-xs"
                        >
                            {trans('form.multi_image_upload.cover')}
                        </span>
                    {/if}
                </button>
                <div class="flex justify-center py-xs">
                    <button
                        type="button"
                        class="relative flex items-center gap-xs p-1"
                        on:click|stopPropagation={() =>
                            dispatch('editCrop', { tempId: item.tempId })}
                        title={trans('form.multi_image_upload.crop.edit')}
                    >
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            class="size-m"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            stroke-width="2"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                        >
                            <path d="M6 2v14a2 2 0 0 0 2 2h14" />
                            <path d="M18 22V8a2 2 0 0 0-2-2H2" />
                        </svg>
                    </button>
                    <button
                        type="button"
                        class="relative flex items-center gap-xs p-1"
                        on:click|stopPropagation={() =>
                            dispatch('editFocalPoint', { tempId: item.tempId })}
                        title={trans(
                            'form.multi_image_upload.focal_point.title',
                        )}
                    >
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            class="size-m"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            stroke-width="2"
                        >
                            <circle cx="12" cy="12" r="10" />
                            <circle cx="12" cy="12" r="3" fill="currentColor" />
                        </svg>
                    </button>
                    <button
                        type="button"
                        class="relative flex items-center gap-xs p-1"
                        on:click|stopPropagation={() =>
                            openDeleteModal(item.tempId)}
                    >
                        <VpDeleteBinIcon class="size-m text-status-negative" />
                        <span class="text-sm uppercase text-status-negative">
                            {trans('common.delete')}
                        </span>
                    </button>
                </div>
                {@const resBadge = getResolutionBadge(item.tempId)}
                {#if resBadge}
                    <div
                        class="flex flex-wrap justify-center gap-2xs px-xs pb-xs"
                    >
                        <span
                            class={cn(
                                'px-xs py-2xs text-xs font-medium',
                                resBadge.class,
                            )}
                        >
                            {resBadge.label}
                        </span>
                    </div>
                {/if}
            {/if}
        </li>
    {/each}
</ul>

<DeleteConfirmationModal
    isOpen={isDeleteModalOpen}
    on:confirm={handleDeleteConfirm}
    on:cancel={handleDeleteCancel}
/>
